<template>
  <div class="public-shop">
    <header class="shop-header" :style="{ background: themeGradient }">
      <div class="container py-3">
        <router-link :to="shopPath('/keranjang')" class="text-white" style="text-decoration:none;">
          <i class="fas fa-arrow-left"></i> Kembali ke Cart
        </router-link>
      </div>
    </header>

    <div class="container mt-4">
      <h3>💳 Checkout</h3>

      <div v-if="cart.length === 0" class="empty-state">
        <i class="fas fa-shopping-cart empty-state-icon"></i>
        <h4>Keranjang kosong</h4>
        <p class="text-muted">Tambahkan produk dulu sebelum checkout.</p>
        <router-link :to="shopPath('')" class="btn btn-primary">Belanja Sekarang</router-link>
      </div>
      <div v-else class="row">
        <div class="col-md-7">
          <div class="card p-4 mb-3">
            <h5>Data Customer</h5>
            <div class="row">
              <div class="col-md-12 mb-2">
                <label>Nama Lengkap *</label>
                <input v-model="form.customer_name" class="form-control" required>
              </div>
              <div class="col-md-6 mb-2">
                <label>Nomor WhatsApp *</label>
                <input v-model="form.customer_phone" class="form-control" placeholder="08xxxx" required>
              </div>
              <div class="col-md-6 mb-2">
                <label>Email *</label>
                <input v-model="form.customer_email" type="email" class="form-control" required>
              </div>
            </div>
          </div>

          <div v-if="hasPhysical" class="card p-4 mb-3">
            <h5>Alamat Pengiriman</h5>
            <label>Alamat Lengkap</label>
            <textarea v-model="form.shipping_address" class="form-control mb-2" rows="3" placeholder="Jl. Anggrek No. 12, RT 1/RW 5..."></textarea>

            <div class="row">
              <div class="col-md-6">
                <label>Cari Kota/Kecamatan Tujuan</label>
                <input v-model="destSearch" class="form-control mb-2" placeholder="ketik kota..." @input="searchDest">
                <div v-if="destResults.length > 0" class="border rounded mb-2" style="max-height:200px; overflow-y:auto;">
                  <div v-for="d in destResults" :key="d.id" class="p-2 border-bottom" style="cursor:pointer"
                    @click="selectDest(d)">
                    {{ d.subdistrict_name }}, {{ d.city_name }}, {{ d.province_name }}
                  </div>
                </div>
                <small v-if="selectedDest" class="text-success">✓ {{ selectedDest.subdistrict_name }}, {{ selectedDest.city_name }}</small>
              </div>
              <div class="col-md-6">
                <label>Kurir</label>
                <div v-if="loadingOngkir" class="text-center p-2">
                  <div class="spinner-border spinner-border-sm"></div> Menghitung ongkir...
                </div>
                <select v-else-if="courierOptions.length > 0" v-model="selectedCourier" class="form-control mb-2">
                  <option value="" disabled>-- Pilih Kurir --</option>
                  <option v-for="c in courierOptions" :key="c.code" :value="c.code">
                    {{ c.name }} ({{ formatRupiah(c.cost) }}{{ c.etd ? ' • ' + c.etd : '' }})
                  </option>
                </select>
                <div v-else-if="selectedDest" class="text-muted small p-2">
                  <i class="fas fa-info-circle"></i> Pilih kecamatan tujuan dulu, lalu kurir akan muncul otomatis.
                  <button class="btn btn-link btn-sm p-0 ml-1" @click="calcOngkir">Coba lagi</button>
                </div>
                <div v-else class="text-muted small p-2">
                  Pilih kecamatan tujuan dulu di kolom kiri
                </div>
              </div>
            </div>
          </div>

          <div class="card p-4 mb-3">
            <h5>Catatan (Opsional)</h5>
            <textarea v-model="form.notes" class="form-control" rows="2"></textarea>
          </div>

          <div class="card p-4 mb-3 shop-card">
            <h5>Metode Pembayaran</h5>
            <PaymentChannelPicker
              :channels="paymentChannels"
              :loading="loadingChannels"
              v-model="form.payment_method"
            />
          </div>
        </div>

        <div class="col-md-5">
          <div class="card p-4 mb-3" style="position:sticky; top:20px;">
            <h5>Ringkasan Pesanan</h5>
            <div v-for="(item, i) in cart" :key="i" class="d-flex justify-content-between mb-2 small">
              <span>{{ item.product_name }} × {{ item.quantity }}</span>
              <span>{{ formatRupiah(item.price * item.quantity) }}</span>
            </div>
            <hr>
            <div class="d-flex justify-content-between mb-1">
              <span>Subtotal:</span>
              <span>{{ formatRupiah(subtotal) }}</span>
            </div>
            <div class="d-flex justify-content-between mb-1">
              <span>Ongkir:</span>
              <span>
                <span v-if="freeShippingApplied" class="text-success"><s>{{ formatRupiah(rawOngkir) }}</s> GRATIS</span>
                <span v-else>{{ formatRupiah(ongkir) }}</span>
              </span>
            </div>
            <div v-if="freeShippingMin > 0 && !freeShippingApplied" class="small text-muted mb-2">
              Belanja min {{ formatRupiah(freeShippingMin) }} untuk free ongkir
            </div>
            <hr>
            <div class="d-flex justify-content-between">
              <b>Total:</b>
              <b class="text-primary">{{ formatRupiah(total) }}</b>
            </div>

            <button class="btn btn-success btn-lg btn-block mt-3" @click="submit" :disabled="submitting || !canSubmit">
              <span v-if="submitting" class="spinner-border spinner-border-sm mr-2"></span>
              🛒 Buat Pesanan
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import publicShopPaths from './public_shop_paths';
import PaymentChannelPicker from '@/components/shop_public/PaymentChannelPicker.vue';
import { applyShopTheme, shopHeaderBackground, shopPublicMixin } from '@/components/shop_public/shop_public_utils';
import { trackInitiateCheckout } from '@/components/shop_public/shop_tracking';
import '@/components/shop_public/shop_public.css';

export default {
  mixins: [publicShopPaths, shopPublicMixin],
  components: { PaymentChannelPicker },
  data() {
    return {
      submitting: false,
      destSearch: '',
      destResults: [],
      selectedDest: null,
      selectedCourier: '',
      courierOptions: [],
      rawOngkir: 0,
      ongkir: 0,
      loadingOngkir: false,
      form: {
        customer_name: '', customer_phone: '', customer_email: '',
        shipping_address: '',
        notes: '',
        payment_method: '',
      },
      destSearchTimer: null,
      paymentChannels: [],
      loadingChannels: false,
    };
  },
  computed: {
    ...mapState({ cart: s => s.public_shop.cart, shop: s => s.public_shop.shop }),
    ...mapGetters({ subtotal: 'public_shop/cartTotal' }),
    themeGradient() { return shopHeaderBackground(this.shop); },
    hasPhysical() { return this.cart.some(i => i.type === 'physical'); },
    freeShippingMin() { return this.shop?.free_shipping_min || 0; },
    freeShippingApplied() {
      return this.freeShippingMin > 0 && this.subtotal >= this.freeShippingMin && this.hasPhysical;
    },
    total() { return this.subtotal + this.ongkir; },
    canSubmit() {
      return this.form.customer_name && this.form.customer_phone && this.form.customer_email && this.form.payment_method &&
        (!this.hasPhysical || (this.form.shipping_address && this.selectedDest && this.selectedCourier));
    },
  },
  mounted() {
    this.$store.dispatch('public_shop/cart_load', this.effectiveSlug);
    if (!this.shop) this.$store.dispatch('public_shop/public_get_shop', this.effectiveSlug);
    this.loadPaymentChannels();
    if (this.cart.length > 0) {
      trackInitiateCheckout(this.cart, this.subtotal);
    }
  },
  watch: {
    shop: {
      immediate: true,
      handler(s) { if (s) applyShopTheme(s); },
    },
    selectedCourier(v) {
      const c = this.courierOptions.find(x => x.code === v);
      this.rawOngkir = c ? c.cost : 0;
      this.applyFreeShipping();
    },
    subtotal() { this.applyFreeShipping(); },
  },
  methods: {
    applyFreeShipping() {
      if (this.freeShippingApplied) {
        this.ongkir = 0;
      } else {
        this.ongkir = this.rawOngkir;
      }
    },
    parseCourierSelection() {
      if (!this.selectedCourier) return { courier: null, service: null };
      const idx = this.selectedCourier.indexOf('_');
      if (idx <= 0) return { courier: this.selectedCourier, service: null };
      return {
        courier: this.selectedCourier.substring(0, idx),
        service: this.selectedCourier.substring(idx + 1),
      };
    },
    async loadPaymentChannels() {
      this.loadingChannels = true;
      try {
        const axios = require('axios');
        const base = (axios.defaults.baseURL || '').replace(/\/api\/v1\/?$/, '');
        const r = await axios.get(base + '/api/public/shop/' + this.effectiveSlug + '/payment-channels', { headers: { Authorization: '' } });
        this.paymentChannels = r.data.data.channels || [];
      } catch (e) { /* noop */ }
      this.loadingChannels = false;
    },
    selectChannel(ch) {
      this.form.payment_method = ch.code;
    },
    searchDest() {
      clearTimeout(this.destSearchTimer);
      if (this.destSearch.length < 3) { this.destResults = []; return; }
      this.destSearchTimer = setTimeout(async () => {
        try {
          const r = await this.$store.dispatch('public_shop/public_search_destination', { slug: this.effectiveSlug, q: this.destSearch });
          this.destResults = r.data.data.destinations || [];
        } catch (e) { this.destResults = []; }
      }, 500);
    },
    selectDest(d) {
      this.selectedDest = d;
      this.destResults = [];
      this.destSearch = `${d.subdistrict_name}, ${d.city_name}`;
      this.courierOptions = [];
      this.selectedCourier = '';
      this.rawOngkir = 0;
      this.ongkir = 0;
      this.calcOngkir();
    },
    async calcOngkir() {
      if (!this.selectedDest) return;
      this.loadingOngkir = true;
      const totalWeight = this.cart.reduce((s, i) => s + (i.weight_grams || 0) * i.quantity, 0) || 1000;
      try {
        const r = await this.$store.dispatch('public_shop/public_get_shipping_cost', {
          slug: this.effectiveSlug,
          payload: {
            destination_subdistrict_id: parseInt(this.selectedDest.id),
            weight_grams: totalWeight,
          },
        });
        const costs = r.data.data.costs || [];
        this.courierOptions = costs.map(cs => ({
          code: cs.code + '_' + cs.service,
          name: cs.name + ' - ' + cs.service,
          cost: cs.cost || 0,
          etd: cs.etd || '',
        }));
        this.courierOptions.sort((a, b) => a.cost - b.cost);
        if (this.courierOptions.length > 0) {
          this.selectedCourier = this.courierOptions[0].code;
          this.rawOngkir = this.courierOptions[0].cost;
          this.applyFreeShipping();
        } else {
          this.$toast.warning('Tidak ada kurir tersedia untuk tujuan ini');
        }
      } catch (e) {
        this.$toast.error('Gagal hitung ongkir: ' + (e.response?.data?.message || e.message));
      }
      this.loadingOngkir = false;
    },
    async submit() {
      this.submitting = true;
      try {
        const { courier, service } = this.parseCourierSelection();
        const payload = {
          customer_name: this.form.customer_name,
          customer_email: this.form.customer_email,
          customer_phone: this.form.customer_phone,
          shipping_address: this.form.shipping_address || null,
          shipping_cost: this.ongkir,
          shipping_courier: courier,
          shipping_service: service,
          destination_subdistrict_id: this.selectedDest ? parseInt(this.selectedDest.id) : null,
          destination_city_name: this.selectedDest ? this.selectedDest.city_name : null,
          payment_method: this.form.payment_method,
          notes: this.form.notes || null,
          items: this.cart.map(i => ({
            product_id: i.product_id,
            quantity: i.quantity,
            selected_variants: i.selected_variants ? JSON.stringify(i.selected_variants) : null,
          })),
        };
        const r = await this.$store.dispatch('public_shop/public_create_order', { slug: this.effectiveSlug, payload });
        const order = r.data.data.order;
        this.$store.dispatch('public_shop/cart_clear');
        this.$router.push(this.shopPath('/order/' + order.order_id));
      } catch (e) { this.$toast.error(e.response?.data?.message || 'Gagal buat pesanan'); }
      this.submitting = false;
    },
    formatRupiah(v) { return 'Rp ' + (v || 0).toLocaleString('id-ID'); },
  },
};
</script>

<style scoped>
.public-shop { background: #f4f6f8; min-height: 100vh; padding-bottom: 40px; }
.shop-header { color: white; }
</style>
